import React, {
	Dispatch,
	RefAttributes,
	useCallback,
	useMemo,
	useRef,
	useState,
	useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { react } from "uikit";
import { assign, clone } from "lodash";

import MultiSelect from "../../../../../../../../../../../../common/LocalMultiSelect";
import { Language } from "../../../../../../../../../../../../../services";
import { useCompanyAndTaxiServiceIdsFilter } from "../../../../../../../../../../../../../hooks";
import { getTaxiServiceLabel } from "../../../../../../../../../../../../../utils";
import LabeledField from "../../../../../../../../../../../../LabeledField";
import { useAppContext } from "../../../../../../../../../../../../../context";

import InternalController from "./Controller";

const TaxiServices = react.withController<
	TaxiServices.Props,
	TaxiServices.Controller
>(({ controller, value, disabled = false, language, onChange }) => {
	const { t } = useTranslation();
	const { getTaxiServiceIds } = useAppContext();
	const currentValueRef = useRef(value);

	const possibleCompaniesRef = useRef<any[]>([]);
	const possibleTaxiServicesRef = useRef<any[]>([]);

	currentValueRef.current = value;

	const setField = useCallback(
		<Field extends keyof TaxiServices.Value>(
			field: Field,
			fieldValue: TaxiServices.Value[Field],
		) => {
			currentValueRef.current = assign(clone(currentValueRef.current), {
				[field]: fieldValue,
			});

			onChange(currentValueRef.current);
		},
		[onChange],
	);

	const onChangeCompanyIds = useCallback(
		(ids: number[] | ["all"]) => {
			setCompanyIdsError(false);

			const isAll = ids.some((id: number | "all") => id === "all");

			if (!isAll) {
				setField("companyIds", ids);
				const allIds: number[] = [];
				ids.forEach((id) => {
					const v = getTaxiServiceIds(id);
					if (v && v?.length) allIds.push(...v);
				});
				setField("taxiServiceIds", allIds);
			}
			if (isAll) {
				const companyIds = possibleCompaniesRef.current.map(
					(c) => c?.id,
				);

				setField("companyIds", companyIds);
				const allIds: number[] = [];
				companyIds.forEach((id) => {
					const v = getTaxiServiceIds(id);
					if (v && v?.length) allIds.push(...v);
				});
				setField("taxiServiceIds", allIds);
			}
		},
		[getTaxiServiceIds, setField],
	);

	const onChangeTaxiServiceIds = useCallback(
		(ids: number[] | ["all"]) => {
			setTaxiServiceIdsError(false);

			const isAll = ids.some((id: number | "all") => id === "all");

			if (!isAll) setField("taxiServiceIds", ids);
			if (isAll) {
				const allIds: number[] = [];
				const possibleIds =
					possibleTaxiServicesRef.current?.map((ts) => ts?.id) || [];
				currentValueRef.current.companyIds.forEach((id) => {
					const v = getTaxiServiceIds(id)?.filter((i) =>
						possibleIds.includes(i),
					);
					if (v && v?.length) allIds.push(...v);
				});

				setField("taxiServiceIds", allIds);
			}
		},
		[setField, getTaxiServiceIds],
	);

	const [
		possibleCompanies,
		possibleTaxiServices,
		companyIds,
		taxiServiceIds,
		setCompanyIds,
		setTaxiServiceIds,
	] = useCompanyAndTaxiServiceIdsFilter(
		value.companyIds,
		value.taxiServiceIds,
		onChangeCompanyIds,
		onChangeTaxiServiceIds,
	);

	possibleCompaniesRef.current = possibleCompanies;
	possibleTaxiServicesRef.current = possibleTaxiServices;

	const [companyIdsError, setCompanyIdsError] = useState(false);

	const possibleCompanyOptions = useMemo(
		() =>
			possibleCompanies.map((company) => ({
				key: company.id,
				label: company.name?.[language] ?? "",
				value: company.id,
			})),
		[language, possibleCompanies],
	);

	const companyIdsOnChange = useCallback(
		(newCompanyIds: MultiSelect.Value) => {
			setCompanyIds(newCompanyIds as number[]);
		},
		[setCompanyIds],
	);

	const [taxiServiceIdsError, setTaxiServiceIdsError] = useState(false);

	const possibleTaxiServiceOptions = useMemo(
		() =>
			possibleTaxiServices.map((taxiService) => ({
				key: taxiService.id,
				label: getTaxiServiceLabel(taxiService, language),
				value: taxiService.id,
			})),
		[language, possibleTaxiServices],
	);

	const taxiServiceIdsOnChange = useCallback(
		(newCompanyIds: MultiSelect.Value) => {
			setTaxiServiceIds(newCompanyIds as number[]);
		},
		[setTaxiServiceIds],
	);

	useEffect(() => {
		if (possibleCompanyOptions.length === 1)
			setCompanyIds([possibleCompanyOptions[0].key]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [possibleCompanyOptions]);
	useEffect(() => {
		if (possibleTaxiServiceOptions.length === 1)
			setTaxiServiceIds([possibleTaxiServiceOptions[0].key]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [possibleTaxiServiceOptions]);

	controller.setContext({
		value,
		setCompanyIdsError,
		setTaxiServiceIdsError,
	});

	return (
		<>
			<LabeledField
				label={
					t(
						"orderPageWidgets.map2.header.filters.content.form.taxiServices.str0",
					) ?? ""
				}
			>
				<MultiSelect
					value={companyIds}
					disabled={disabled || possibleCompanyOptions.length === 1}
					all={possibleCompanyOptions.length > 1}
					placeholder={
						t(
							"orderPageWidgets.map2.header.filters.content.form.taxiServices.str0",
						) ?? ""
					}
					error={companyIdsError}
					options={possibleCompanyOptions}
					onChange={companyIdsOnChange}
				/>
			</LabeledField>
			<LabeledField
				label={
					t(
						"orderPageWidgets.map2.header.filters.content.form.taxiServices.str2",
					) ?? ""
				}
			>
				<MultiSelect
					value={taxiServiceIds}
					disabled={
						disabled || possibleTaxiServiceOptions.length <= 1
					}
					all={possibleTaxiServiceOptions.length > 1}
					placeholder={
						t(
							"orderPageWidgets.map2.header.filters.content.form.taxiServices.str2",
						) ?? ""
					}
					error={taxiServiceIdsError}
					options={possibleTaxiServiceOptions}
					onChange={taxiServiceIdsOnChange}
				/>
			</LabeledField>
		</>
	);
}, InternalController);

declare namespace TaxiServices {
	type Ref = InternalController | null;

	type Controller = InternalController;

	interface Value {
		companyIds: number[] | ["all"];
		taxiServiceIds: number[] | ["all"];
	}

	interface PropsBase {
		value: Value;

		disabled?: boolean;
		language: Language;

		onChange: Dispatch<Value>;
	}

	type Props = PropsBase & RefAttributes<Ref>;
}

export default TaxiServices;
